import React, {useCallback} from 'react';
import {Info, RotateCcw} from 'lucide-react';
import {BaseFormComponent} from "../DynamicFormTypes.ts";
import {FieldInfoResponse, INFO_TYPE} from "../../../api/ApiTypes.ts";
import {useFormInfo, useFormMethods} from "../DyanmicFormContext.tsx";
import {useWatch} from "react-hook-form";

interface FieldInfoIconProps {
    component: BaseFormComponent
}

const FieldInfoIconRenderer: React.FC<FieldInfoIconProps> = ({ component }) => {
    const { formMethods: { control } } = useFormMethods();
    const { clearInfo } = useFormInfo();

    const convertedKey = `_info.${component.fieldKey.replace(/\./g, "_")}`;
    const rawValue = useWatch({ name: convertedKey, control, defaultValue: undefined });

    // Direct key access — no array conversion needed since keys are known enum values
    const record = (rawValue !== null && typeof rawValue === "object" && !Array.isArray(rawValue))
        ? rawValue as Partial<Record<INFO_TYPE, FieldInfoResponse>>
        : undefined;

    const overrideEntry = record?.[INFO_TYPE.OVERRIDE];
    const calculatedEntry = record?.[INFO_TYPE.CALCULATED];
    const defaultEntry = record?.[INFO_TYPE.DEFAULT];
    const providedEntry = record?.[INFO_TYPE.PROVIDED];

    const isOverridden = !!(overrideEntry);
    const hasContent = isOverridden || !!(calculatedEntry?.content) || !!(defaultEntry?.content) || !!(providedEntry?.content);

    const handleClick = useCallback(() => {
        clearInfo(component.fieldKey, INFO_TYPE.OVERRIDE);
    }, [clearInfo, component.fieldKey]);

    if (!hasContent) return null;

    return (
        <div className="relative mx-1 group/info z-30 self-start flex items-start">
            {isOverridden ? (
                <button
                    type="button"
                    tabIndex={-1}
                    className="group/btn text-amber-500 hover:text-amber-600 focus:outline-none"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleClick}
                >
                    <Info size={16} className="group-hover/btn:hidden" />
                    <RotateCcw size={16} className="hidden group-hover/btn:block" />
                </button>
            ) : (
                <button
                    type="button"
                    tabIndex={-1}
                    className="text-indigo-500 hover:text-indigo-700 focus:outline-none"
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <Info size={16} />
                </button>
            )}

            <div
                id={component.fieldKey + "-info"}
                role="tooltip"
                className={
                    "pointer-events-none opacity-0 " +
                    "group-hover/info:opacity-100 group-focus-within/info:opacity-100 " +
                    "transition-opacity " +
                    "absolute bottom-full w-fit p-1 mt-1 rounded-md shadow-lg text-xs " +
                    (isOverridden ? "bg-amber-500 " : "bg-indigo-500 ") +
                    (component.compactUI ? "left-full" : "right-full")
                }
            >
                {isOverridden && (
                    <TooltipSection title="Override Active">
                        <p>Manually overridden</p>
                        {calculatedEntry?.content && (
                            <p className="opacity-80">Calculated: {calculatedEntry.content}</p>
                        )}
                        <p className="opacity-60 italic">Click to restore.</p>
                    </TooltipSection>
                )}
                {!isOverridden && calculatedEntry?.content && (
                    <TooltipSection title={calculatedEntry.title ?? "Calculated"}>
                        <p>{calculatedEntry.content}</p>
                    </TooltipSection>
                )}
                {!isOverridden && defaultEntry?.content && (
                    <TooltipSection title={defaultEntry.title ?? "Info"}>
                        <p className="whitespace-pre-line">{defaultEntry.content}</p>
                    </TooltipSection>
                )}
                {!isOverridden && providedEntry?.content && (
                    <TooltipSection title={providedEntry.title ?? "Provided: "}>
                        <p className="whitespace-pre-line">{providedEntry.content}</p>
                    </TooltipSection>
                )}
            </div>
        </div>
    );
};

interface TooltipSectionProps {
    title: string;
    children: React.ReactNode;
}

const TooltipSection: React.FC<TooltipSectionProps> = ({ title, children }) => (
    <div className="px-2 py-1">
        <h4 className="font-semibold text-white mb-0.5">{title}</h4>
        <div className="bg-white text-black p-2 rounded-xs">
            {children}
        </div>
    </div>
);

export const FieldInfoIcon = React.memo(FieldInfoIconRenderer);
